import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { and, or, ternaryResolver } from "@/Common/helpers/sonar";
import { getSections, getSectionsAvailable } from "@PayPlans/helpers/sections";
import { getMappedOEMFranchises } from "@/Common/helpers/OEMs";

// Hooks
import { useLocationFS } from "@/Common/hooks/LocationFS";
import { getExtendedBranchesFranchises, getOEMFranchisePrograms } from "@/Common/hooks/LocationPrograms";

// Defaults builders
import {
	DefaultELRBonus,
	DefaultHROBonus,
	DefaultRetentionPay,
	CallActivityPerformanceDefault,
	CustomerSatisfactionDefault,
	FollowUpPerformanceBonusDefault,
	LongevityDefault,
	TopPerformanceBonusDefault,
	HolidayPayDefault,
} from "@PayPlans/helpers/DefaultSections";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Components
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import SectionContainer from "@Layouts/components/SectionContainer";

// Custom Sections
import {
	CallActivityPerformance,
	CustomerSatisfaction,
	DefaultCommissions,
	RetentionPay,
	TopAdvisorsBonus,
	Longevity,
	FollowUpPerformance,
	ElrHro,
	HolidayPay,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

const allSections = getSections(
	[
		"topAdvisorsBonus",
		"longevity",
		"callActivityPerformance",
		"customerSatisfaction",
		"retentionPay",
		"followUpPerformanceBonus",
		"elr",
		"hro",
		"holidayPay",
	],
	[{ section: "topAdvisorsBonus", modify: { title: "Top Advisor(s) Bonus", description: "Note for top advisors bonus" } }]
);

// Used to build franchises dependant sections
const excludesMakes = ["Cadillac"];
const checkPrograms = ["sfe", "pinnacle"];

// Constants
const LAZY_TIME = 500;
const contentAlignment = "flex-start";

const OPTIONAL_SECTIONS = new Map([
	["callActivityPerformance", CallActivityPerformanceDefault],
	["longevity", LongevityDefault],
	["customerSatisfaction", CustomerSatisfactionDefault],
	["topAdvisorsBonus", TopPerformanceBonusDefault],
	["followUpPerformanceBonus", FollowUpPerformanceBonusDefault],
	["retentionPay", DefaultRetentionPay],
	["elr", DefaultELRBonus],
	["hro", DefaultHROBonus],
	["holidayPay", HolidayPayDefault],
]);

const isSpecialUpdateSection = (section) => ["customerSatisfaction"].includes(section);

const getSectionsToRemove = (sections = []) => checkPrograms.filter((program) => !sections?.find((item) => item?.program === program));
const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

const isReadOnlyValue = (loading, data, position) => !loading && (Boolean(data?.available) || position !== data?.position);

export default function PayPlansModel(props) {
	const { data = null, location = null, isNew = false, loading = false, readOnly = false, updateModel = () => {} } = props;

	// Hooks
	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();
	const { data: locationFS, isLoading: loadingFS } = useLocationFS({ locationId: location.locationId });

	const isReadOnly = isReadOnlyValue(loading, data, position);

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());

	// Programs usage
	const programsUsage = useMemo(() => {
		const extendedBranchesFranchises = getExtendedBranchesFranchises(location?.branchesOEMs, location?.branchesFranchises);
		return getOEMFranchisePrograms(locationFS?.oems, extendedBranchesFranchises);
	}, [location, locationFS]);

	// Sections to remove
	const removeSections = useMemo(() => getSectionsToRemove(programsUsage), [programsUsage]);

	// Sections to disable
	const disableSections = programsUsage?.filter((item) => item.disabled).map((item) => item.program) ?? [];

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		const isCSI = ["customerSatisfaction"].includes(key);
		if (isCSI) {
			const mappedOEMFranchises = getMappedOEMFranchises(location?.branchesOEMs ?? [], location?.branchesFranchises ?? []);
			data[key] = sectionFn(mappedOEMFranchises ?? [], excludesMakes);
		} else {
			data[key] = sectionFn();
		}

		setSections(getSectionsAvailable(allSections, data, removeSections));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data, removeSections));
		updateModel({ section, data: data[section] });
	};

	// Disabling Sonar scan for the next line is necessary since using an object instead would
	// require a lot of changes since this function is used in multiple places
	const handleUpdateSection = (section, update = null, value = null, isValidSection = true) => {
		// NOSONAR
		if (isSpecialUpdateSection(section)) {
			if (String(update) === "weighting") {
				data[section][update] = value;
			} else {
				data[section]["makeMetrics"]?.forEach((makeMetric, key) => {
					if (makeMetric.make === update) {
						data[section]["makeMetrics"][key] = value;
					}
				});
			}
		} else {
			data[section] = value;
		}

		// Update model
		Boolean(isValidSection) && updateModel({ section, data: data[section] });
	};

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data, removeSections));
	}, [data, removeSections]);

	const isDOMLoading = useMemo(() => or(isLazyLoading, loading, loadingFS), [isLazyLoading, loading, loadingFS]);

	useMemo(() => {
		setIsLazyLoading(true);
		if (selected) {
			setTimeout(() => {
				setIsLazyLoading(false);
			}, LAZY_TIME);
		}
	}, [selected, setIsLazyLoading]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	const isReadOnlyReason = or(readOnly, isReadOnly);

	const getElrHroWrapper = ({ section }) => (
		<Collapse sx={{ width: 1, marginTop: 2 }}>
			<SectionContainer
				sectionId={section}
				title={sections.get(section)?.title}
				collapsible
				deletable
				loading={loading}
				readOnly={isReadOnlyReason}
				onDeleteAction={handleDeleteSection}
			>
				<ElrHro
					sectionId={section}
					content={data?.[section]}
					variant={section.toUpperCase()}
					loading={loading}
					readOnly={isReadOnlyReason}
					useSectionsErrors={useSectionsErrors}
					onActionUpdate={handleUpdateSection}
				/>
			</SectionContainer>
		</Collapse>
	);

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disableSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			<TransitionGroup component={Grid} container item xs={12}>
				<Collapse sx={{ width: 1 }}>
					<SectionContainer
						sectionId={"default"}
						title={"Default Commissions"}
						collapsible
						loading={loading}
						readOnly={isReadOnlyReason}
					>
						<DefaultCommissions
							sectionId={"default"}
							content={data?.default}
							errors={errors?.get("default")}
							isPartsAndLabor={Boolean(data?.default?.isPartsAndLabor)}
							isGuaranteedDraw={Boolean(data?.default?.isGuaranteedDraw)}
							loading={loading}
							readOnly={isReadOnlyReason}
							useSectionsErrors={useSectionsErrors}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				{and(Boolean(sections.get("callActivityPerformance")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"callActivityPerformance"}
							title={sections.get("callActivityPerformance").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CallActivityPerformance
								sectionId={"callActivityPerformance"}
								performance={data?.callActivityPerformance}
								loading={loading}
								readOnly={isReadOnlyReason}
								paySwitchIsEditable={false}
								useSectionsErrors={useSectionsErrors}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{and(Boolean(sections.get("followUpPerformanceBonus")), !isDOMLoading) && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"followUpPerformanceBonus"}
							title={sections.get("followUpPerformanceBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<FollowUpPerformance
								sectionId={"followUpPerformanceBonus"}
								content={data?.followUpPerformanceBonus}
								useSectionsErrors={useSectionsErrors}
								loading={isDOMLoading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{ternaryResolver(
					and(Boolean(sections.get("elr")), !isDOMLoading),
					getElrHroWrapper({ section: "elr", validations: [], extraErrors: [] }),
					null
				)}

				{ternaryResolver(
					and(Boolean(sections.get("hro")), !isDOMLoading),
					getElrHroWrapper({ section: "hro", validations: [], extraErrors: [] }),
					null
				)}
			</TransitionGroup>

			<Grid container item xs={12} columnSpacing={2} sx={!loading ? { paddingTop: "0px !important" } : null}>
				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("customerSatisfaction") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"customerSatisfaction"}
									title={sections.get("customerSatisfaction").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<CustomerSatisfaction
										sectionId={"customerSatisfaction"}
										content={data?.customerSatisfaction}
										errors={errors?.get("customerSatisfaction")}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}

				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{and(Boolean(sections.get("holidayPay")), !isDOMLoading) && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"holidayPay"}
									title={sections.get("holidayPay").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<HolidayPay
										content={data?.holidayPay}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
						{and(Boolean(sections.get("retentionPay")), !isDOMLoading) && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"retentionPay"}
									title={sections.get("retentionPay").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<RetentionPay
										sectionId={"retentionPay"}
										content={data?.retentionPay}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
						{and(Boolean(sections.get("topAdvisorsBonus")), !isDOMLoading) && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"topAdvisorsBonus"}
									title={sections.get("topAdvisorsBonus").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<TopAdvisorsBonus
										content={data?.topAdvisorsBonus}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
						{sections.get("longevity") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"longevity"}
									title={sections.get("longevity").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<Longevity
										content={data?.longevity}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}
			</Grid>
		</PayPlansLayout>
	);
}

PayPlansModel.propTypes = {
	data: PropTypes.shape({
		available: PropTypes.bool,
		position: PropTypes.string,
		callActivityPerformance: PropTypes.object,
		customerSatisfaction: PropTypes.object,
		longevity: PropTypes.array,
		volumeBonus: PropTypes.object,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
		oems: PropTypes.array,
		franchises: PropTypes.array,
		branchesFranchises: PropTypes.array,
	}),
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
};
